import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";
import { TApplicationField } from "./application-fields.hooks";
import { formConfigKeyStore } from "./form-step-config.hooks";

export type TForm = {
  id: number;
  type: string;
  name: string;
  active?: boolean;
  updatedAt: string;
  serviceName?: string;
};

export type TFormWithFields = TForm & {
  documentsEnabled?: TDocumentConfig[];
  fieldsEnabled?: TApplicationField[];
};

export type TServiceType = { name: string; id: number };

export type TDocumentConfig = {
  document_config_id: number;
  document_label: string;
  document_name: string;
  document_Type: string;
  mandatory?: number;
  active?: number;
  file_type?: string;
  file_size: number;
  file_size_unit?: string;
  updated_date: string;
};

export type TCreateFormPayload = {
  name?: string;
  active?: boolean;
  type?: string;
  serviceName?: string;
  fieldsEnabled?: number[];
  documentsEnabled?: number[];
};

export type TPatchFormPayload = TCreateFormPayload & { id: number };

const formsKeyStore = {
  createdForms: () => ["created-forms"],
  allowedFields: (type?: string, serviceName?: string) => [
    "allowed-fields",
    type,
    serviceName,
  ],
  serviceTypes: () => ["service-types"],
  formsCount: (...args: (number | string)[]) => ["forms-count", ...args],
  forms: (...args: (number | string)[]) => ["forms", ...args],
  form: (id?: number) => ["form", id],
  formId: (type?: string, serviceName?: string) => [
    "form-id",
    type,
    serviceName,
  ],
};

export function useServiceTypes() {
  return useQuery({
    queryFn: () =>
      _axios.get<{ success: true; data: Array<TServiceType> }>(
        "/forms/service-types"
      ),
    queryKey: formsKeyStore.serviceTypes(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    select: (result) => result.data,
  });
}

export function useGetForms({
  limit,
  page,
  search,
  sort,
  sortVal,
}: {
  page: number;
  limit: number;
  search?: string;
  sort?: string;
  sortVal?: number;
}) {
  return useQuery({
    queryFn: () =>
      _axios.get<{ success: true; data: Array<TForm> }>("/forms", {
        params: { limit, page, search, sort, sortVal },
      }),
    queryKey: formsKeyStore.forms(
      page,
      limit,
      search || "",
      sort || "",
      sortVal || 1
    ),
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    staleTime: 10 * 60 * 1000, // 10 minutes
    select: (result) => result.data,
  });
}

export function useFormsCount({ search }: { search?: string }) {
  return useQuery({
    queryFn: () =>
      _axios.get<{ success: true; data: number }>("/forms/count", {
        params: { search },
      }),
    queryKey: formsKeyStore.formsCount(search || ""),
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    staleTime: 10 * 60 * 1000, // 10 minutes
    select: (result) => result.data,
  });
}

export function useAllowedFields({
  type,
  serviceName,
}: {
  type?: string;
  serviceName?: string;
}) {
  return useQuery({
    queryKey: formsKeyStore.allowedFields(type, serviceName),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: { fields: TApplicationField[]; documents: TDocumentConfig[] };
      }>("/forms/allowed-fields", { params: { type, serviceName } }),
    select: (result) => result.data?.data,
    enabled: !!type && (type === "Service Form" ? !!serviceName : true),
  });
}

export function useCreateForm() {
  const qc = useQueryClient();

  return useMutation<
    { success: boolean },
    AxiosError | Error,
    TCreateFormPayload
  >({
    mutationFn: (data) =>
      _axios.post<{ success: boolean }>(`/forms`, data).then((res) => res.data),
    onError: () => {
      ErrorToast("Something went wrong while creating form!");
    },
    onSuccess: () => {
      SuccessToast("Form added successfully!");
      qc.invalidateQueries({ queryKey: formsKeyStore.forms() });
      qc.invalidateQueries({ queryKey: formsKeyStore.formsCount() });

      qc.invalidateQueries({
        queryKey: formConfigKeyStore.createdFormConfigs(),
      });

      return qc.invalidateQueries({ queryKey: formsKeyStore.createdForms() });
    },
  });
}

export function useAlreadyCreatedForms() {
  return useQuery({
    queryKey: formsKeyStore.createdForms(),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: { type: string; serviceName?: null | string }[];
      }>("/forms/created"),
    select: (result) => result.data?.data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useGetForm(id?: number, ctx = "") {
  return useQuery({
    enabled: !!id,
    queryKey: formsKeyStore.form(id),
    queryFn: () =>
      _axios.get<{ success: true; data: TFormWithFields }>(`/forms/${id}`, {
        params: { ctx },
      }),
    select: (result) => result.data?.data,
  });
}

export function useGetFormIdByType(
  type?: string,
  serviceName?: string,
  enabled = true
) {
  return useQuery({
    enabled:
      enabled && !!type && !!(type === "Service Form" ? serviceName : true),
    queryKey: formsKeyStore.formId(type, serviceName),
    queryFn: () =>
      _axios.get<{ success: true; data: number }>("/forms/by-type", {
        params: { type, serviceName },
      }),
    select: (result) => result.data?.data,
  });
}

export function usePatchForm() {
  const qc = useQueryClient();

  return useMutation<
    { success: boolean },
    AxiosError | Error,
    TPatchFormPayload
  >({
    mutationFn: (data) =>
      _axios
        .patch<{ success: boolean }>(`/forms/${data.id}`, data)
        .then((res) => res.data),
    onError: () => {
      ErrorToast("Something went wrong while creating form!");
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({
        queryKey: formConfigKeyStore.createdFormConfigs(),
      });
      SuccessToast("Form updated successfully!");
      qc.invalidateQueries({ queryKey: formsKeyStore.forms() });
    },
  });
}
